import { isCapacitorShell } from '~~/layers/base/utils/isCapacitorShell'
import { promiseWithTimeout } from '~~/layers/base/utils/promiseWithTimeout'

/**
 * Cap on a native `Network.getStatus` read. The Capacitor bridge is serialized; awaiting it
 * without a timeout from a Nuxt plugin (`better-auth-initial-anonymous`, feature flags) can
 * deadlock with Preferences restore and leave the native splash on screen forever.
 */
const NETWORK_STATUS_TIMEOUT_MS = 400

/**
 * Best-effort "device has no link" check for skipping doomed cold-start fetches.
 *
 * ## Composable-in-util exception (do not copy casually)
 *
 * Nuxt composables (`usePinia`, `useNetworkStore`, and {@link isCapacitorShell} via
 * `useRuntimeConfig`) are normally forbidden outside composables and `setup`. **This module is a
 * deliberate, narrow exception** for Capacitor cold-start plugins that must decide whether to
 * skip doomed fetches *before* the network toast composable has run.
 *
 * Rules for maintainers:
 * - Do **not** call `isDeviceOffline()` from general app code — use {@link useNetworkStore} from
 *   a composable, plugin, or component instead.
 * - Do **not** add new utils that call Pinia/Nuxt composables; fold that logic into a composable
 *   or store action unless you have the same cold-start bridge deadlock constraints documented
 *   here.
 * - Never let this util be the **first** `useNetworkStore()` call (see
 *   {@link networkStoreAlreadyInstalled}).
 *
 * Order:
 * 1. Existing {@link useNetworkStore}.connected already false (does not *create* the store —
 *    instantiating it here would call the Network plugin during other plugins' setup)
 * 2. Capacitor `@capacitor/network` OS status (bounded)
 * 3. `navigator.onLine === false`
 *
 * Steps 1-2 are Capacitor-only. Creating the store attaches listeners and arms the recovery ping
 * loop — on web that would poll the connectivity stub from the designer SPA. The toast composable
 * is what instantiates the store, after plugins have finished.
 *
 * Returns false when unsure (optimistic) so we do not skip fetches on a flaky false-offline read.
 */
export async function isDeviceOffline(): Promise<boolean> {
    if (!import.meta.client) {
        return false
    }

    if (isCapacitorShell()) {
        try {
            // Exception: `usePinia()` in a util — see module docstring; read-only peek, never
            // the first `useNetworkStore()` call.
            const pinia = usePinia()
            if (pinia && networkStoreAlreadyInstalled(pinia)) {
                const { useNetworkStore } = await import('~~/layers/base/stores/network.store')
                const store = useNetworkStore(pinia)
                if (store.connected === false) {
                    return true
                }
            }
        } catch {
            // Pinia / store not ready yet (early plugins) — fall through to OS signals.
        }

        try {
            const { Network } = await import('@capacitor/network')
            const status = await promiseWithTimeout(
                Network.getStatus(),
                NETWORK_STATUS_TIMEOUT_MS,
                null
            )
            if (status == null) {
                // Bridge hung — do not skip fetches; a transport failure can still mark offline.
                return navigatorOnLineIsFalse()
            }
            if (status.connected === false) {
                notifyExistingStoreOffline()
                return true
            }
            return false
        } catch {
            // Native bridge unavailable (Capacitor web preview) — fall through.
        }
    }

    return navigatorOnLineIsFalse()
}

/** `navigator.onLine === false` when the object exists. */
function navigatorOnLineIsFalse(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false
}

/**
 * Pinia only creates a store on first `useStore()`. `isDeviceOffline` must not be that first
 * call: store setup talks to the Network plugin, and doing that while other plugins await
 * Preferences deadlocks the iOS bridge.
 */
function networkStoreAlreadyInstalled(pinia: { _s?: Map<string, unknown> }): boolean {
    return Boolean(pinia._s?.has('network'))
}

/**
 * If the toast composable already created the store, fold this OS-offline read into it so skipped
 * fetches still flip `connected`. Fire-and-forget: do not await native from here.
 *
 * Same composable-in-util exception as {@link isDeviceOffline}; only runs when the store already
 * exists — never the code path that first installs it.
 */
function notifyExistingStoreOffline(): void {
    try {
        // Exception: `usePinia()` in a util — see {@link isDeviceOffline} module docstring.
        const pinia = usePinia() as { _s?: Map<string, unknown> }
        if (!pinia._s?.has('network')) {
            return
        }
        void import('~~/layers/base/stores/network.store').then(({ useNetworkStore }) => {
            useNetworkStore().reportTransportFailure('capacitor-network')
        })
    } catch {
        // Store not available — the toast composable will instantiate it after plugins.
    }
}
